import { StateCreator } from "zustand";
import { UserSlice } from "@/stores/user.slice";
import { ListenersSlice } from "@/stores/listeners.slice";
import { ShopSlice } from "@/stores/shop.slice";
import { CustomerUpdate, CustomerUpdateStatus } from "@/types/v2";
import { customerUpdate as customerUpdateRepository } from "@/repositories/customerUpdates";

export interface CustomerUpdatesSlice {
  myCustomerUpdates: CustomerUpdate[];
  myNextCustomerUpdate: CustomerUpdate | null;
  allMyCustomerUpdates: CustomerUpdate[];
  customerUpdatesBadgeCount: number;
  isCustomerUpdatesTabVisible: boolean;
  subscribeToMyCustomerUpdates: () => void;
  unsubscribeFromMyCustomerUpdates: () => void;
  subscribeToAllMyCustomerUpdates: () => void;
  unsubscribeFromAllMyCustomerUpdates: () => void;
  setCustomerUpdatesTabVisible: (visible: boolean) => void;
}

const initialSlice: Omit<
  CustomerUpdatesSlice,
  | "subscribeToMyCustomerUpdates"
  | "unsubscribeFromMyCustomerUpdates"
  | "subscribeToAllMyCustomerUpdates"
  | "unsubscribeFromAllMyCustomerUpdates"
  | "setCustomerUpdatesTabVisible"
> = {
  myCustomerUpdates: [],
  myNextCustomerUpdate: null,
  allMyCustomerUpdates: [],
  customerUpdatesBadgeCount: 0,
  isCustomerUpdatesTabVisible: false,
};

export const createCustomerUpdatesSlice: StateCreator<
  CustomerUpdatesSlice & ShopSlice & UserSlice & ListenersSlice,
  [],
  [],
  CustomerUpdatesSlice
> = (set, get) => ({
  ...initialSlice,
  subscribeToMyCustomerUpdates: () => {
    const { shopId, user } = get();
    const userId = user?.id;
    if (!shopId || !userId) {
      console.error("Shop ID or User ID is missing");
      return;
    }

    const callback = (updates: CustomerUpdate[] | undefined) => {
      set({
        myCustomerUpdates: updates,
        myNextCustomerUpdate: updates && updates.length > 0 ? updates[0] : null,
      });
    };

    const unsubscribe = customerUpdateRepository.subscribeMine(
      shopId,
      userId,
      callback
    );

    get().setOtherListener("myCustomerUpdates", unsubscribe);
  },
  unsubscribeFromMyCustomerUpdates: () => {
    const unsubscribe = get().unsubscribeOtherListener;
    if (unsubscribe) {
      unsubscribe("myCustomerUpdates");
    }
  },
  subscribeToAllMyCustomerUpdates: () => {
    const { shopId, user } = get();
    const userId = user?.id;
    if (!shopId || !userId) {
      console.error("Shop ID or User ID is missing");
      return;
    }

    const callback = (updates: CustomerUpdate[] | undefined) => {
      const badgeCount =
        updates?.filter(
          (update) =>
            update.status === CustomerUpdateStatus.PENDING ||
            update.status === CustomerUpdateStatus.SNOOZED ||
            update.status === CustomerUpdateStatus.CREATED
        ).length ?? 0;

      set({
        allMyCustomerUpdates: updates ?? [],
        customerUpdatesBadgeCount: badgeCount,
      });
    };

    const unsubscribe = customerUpdateRepository.subscribeAllMine(
      shopId,
      userId,
      callback
    );

    get().setOtherListener("allMyCustomerUpdates", unsubscribe);
  },
  unsubscribeFromAllMyCustomerUpdates: () => {
    const unsubscribe = get().unsubscribeOtherListener;
    if (unsubscribe) {
      unsubscribe("allMyCustomerUpdates");
    }
  },
  setCustomerUpdatesTabVisible: (visible: boolean) => {
    set({ isCustomerUpdatesTabVisible: visible });
  },
});
